import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Check, ExternalLink, X } from "lucide-react";
import { motion } from "framer-motion";
import { useAuthOperations, SignupData } from "@/hooks/useAuthOperations";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FBN_CHAPTERS, SEGMENT_OPTIONS, PROJECT_TYPE_OPTIONS, SDG_GOALS, INDUSTRY_OPTIONS } from "@/data/formOptions";

const signupSchema = z.object({
  email: z.string().trim().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
  firstName: z.string().trim().min(1, "First name is required").max(50, "First name is too long"),
  lastName: z.string().trim().min(1, "Last name is required").max(50, "Last name is too long"),
  fbnChapter: z.string().min(1, "Please select your FBN chapter"),
  city: z.string().trim().max(100, "City must be less than 100 characters").optional(),
  segment: z.array(z.string()).optional(),
  bio: z.string().max(500, "Bio must be less than 500 characters").optional(),
  workingOnProject: z.boolean().optional(),
  projectType: z.string().optional(),
  projectDescription: z.string().max(500, "Description must be less than 500 characters").optional(),
  sdgGoals: z.array(z.string()).optional(),
  industryInterests: z.array(z.string()).optional(),
  linkedinUrl: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  websiteUrl: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  phoneNumber: z.string().optional().or(z.literal("")),
  legalAcceptanceConfirmed: z.literal(true, {
    errorMap: () => ({ message: "You must accept the documents to continue" }),
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

export const SignupForm = () => {
  const navigate = useNavigate();
  const { signUp, loading } = useAuthOperations();
  const [success, setSuccess] = useState(false);
  const [customIndustry, setCustomIndustry] = useState("");
  const [showCustomIndustry, setShowCustomIndustry] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent">("idle");

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      fbnChapter: "",
      city: "",
      segment: [],
      bio: "",
      workingOnProject: false,
      projectType: "",
      projectDescription: "",
      sdgGoals: [],
      industryInterests: [],
      linkedinUrl: "",
      websiteUrl: "",
      phoneNumber: "",
      legalAcceptanceConfirmed: false as unknown as true,
    },
  });

  const workingOnProject = form.watch("workingOnProject");

  const onSubmit = async (values: SignupFormValues) => {
    const data: SignupData = {
      email: values.email,
      password: values.password,
      firstName: values.firstName,
      lastName: values.lastName,
      fbnChapter: values.fbnChapter,
      city: values.city,
      segment: values.segment,
      bio: values.bio,
      workingOnProject: values.workingOnProject,
      projectType: values.projectType,
      projectDescription: values.projectDescription,
      sdgGoals: values.sdgGoals,
      industryInterests: values.industryInterests,
      linkedinUrl: values.linkedinUrl || undefined,
      websiteUrl: values.websiteUrl || undefined,
      phoneNumber: values.phoneNumber || undefined,
    };

    const result = await signUp(data);
    if (result.success) {
      // Legal acceptance is recorded by AuthContext on the first SIGNED_IN
      // event AFTER email confirmation. We can't call the edge function here
      // because email confirmation is required and `auth.signUp` does not
      // issue a session in that case — the function's JWT check would reject.
      // The `pending_legal_acceptance: true` flag set in user_metadata is the
      // signal the listener uses to record acceptances and clear the flag.
      setSubmittedEmail(values.email);
      setSuccess(true);
    }
  };

  const toggleArrayItem = (array: string[] | undefined, item: string): string[] => {
    const current = array || [];
    return current.includes(item)
      ? current.filter((i) => i !== item)
      : [...current, item];
  };

  if (success) {
    const handleResend = async () => {
      if (!submittedEmail || resendStatus !== "idle") return;
      setResendStatus("sending");
      try {
        await supabase.auth.resend({ type: "signup", email: submittedEmail });
        setResendStatus("sent");
      } catch {
        setResendStatus("idle");
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground mb-3">
          Check Your Email
        </h3>
        <p className="text-muted-foreground mb-2 max-w-sm mx-auto">
          We've sent a verification link to:
        </p>
        <p className="text-sm font-medium text-foreground mb-4 break-words">
          {submittedEmail}
        </p>
        <p className="text-xs text-muted-foreground mb-6 max-w-sm mx-auto">
          Click the link in the email to activate your account. It may take a minute to arrive — and check your spam folder if you don't see it.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button
            variant="outline"
            onClick={handleResend}
            disabled={resendStatus !== "idle"}
          >
            {resendStatus === "sending" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : resendStatus === "sent" ? (
              "Verification email sent"
            ) : (
              "Resend verification email"
            )}
          </Button>
          <Button variant="ghost" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
        {/* Section 1: Account Credentials */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Account Setup
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password *</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Min 8 characters"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password *</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Repeat password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </section>

        {/* Section 2: Identity */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Your Identity
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="John" autoComplete="given-name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" autoComplete="family-name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="fbnChapter"
            render={({ field }) => (
              <FormItem>
                <FormLabel>FBN Chapter *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your chapter" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {FBN_CHAPTERS.map((chapter) => (
                      <SelectItem key={chapter} value={chapter}>
                        {chapter}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Helsinki" autoComplete="address-level2" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </section>

        {/* Section 3: Professional Profile */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Your Role
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormField
            control={form.control}
            name="segment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role(s)</FormLabel>
                <FormDescription>Select all that apply</FormDescription>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SEGMENT_OPTIONS.map((segment) => {
                    const isSelected = field.value?.includes(segment);
                    return (
                      <button
                        key={segment}
                        type="button"
                        onClick={() => field.onChange(toggleArrayItem(field.value, segment))}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          isSelected
                            ? "bg-primary text-primary-foreground shadow-soft"
                            : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
                        }`}
                      >
                        {segment}
                      </button>
                    );
                  })}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bio"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Short Bio</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Tell us about yourself and your entrepreneurial journey..."
                    className="min-h-[100px] resize-none"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {(field.value?.length || 0)}/500 characters
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </section>

        {/* Section 4: Project Information */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Current Project
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormField
            control={form.control}
            name="workingOnProject"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border p-4 bg-card">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Working on a project?</FormLabel>
                  <FormDescription>
                    Are you working on a project inside or outside of your family business?
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {workingOnProject && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 overflow-hidden"
            >
              <FormField
                control={form.control}
                name="projectType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="What kind of project?" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PROJECT_TYPE_OPTIONS.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="projectDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Briefly describe your project..."
                        className="min-h-[100px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      This will appear on your member card. {(field.value?.length || 0)}/500 characters
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>
          )}
        </section>

        {/* Section 5: Impact Focus */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Impact Focus
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormField
            control={form.control}
            name="sdgGoals"
            render={({ field }) => (
              <FormItem>
                <FormLabel>SDG Goals</FormLabel>
                <FormDescription>
                  Which UN Sustainable Development Goals align with your work?
                </FormDescription>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                  {SDG_GOALS.map((sdg) => {
                    const isSelected = field.value?.includes(sdg.value);
                    return (
                      <button
                        key={sdg.value}
                        type="button"
                        onClick={() => field.onChange(toggleArrayItem(field.value, sdg.value))}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                          isSelected
                            ? "bg-primary/10 text-primary border-primary/30 border shadow-soft"
                            : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
                        }`}
                      >
                        <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">
                          {sdg.number}
                        </span>
                        <span className="truncate">{sdg.label}</span>
                      </button>
                    );
                  })}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </section>

        {/* Section: Industry Interests */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Industry Interests
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormField
            control={form.control}
            name="industryInterests"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Industries</FormLabel>
                <FormDescription>Which industries are you interested in?</FormDescription>
                <div className="flex flex-wrap gap-2 mt-3">
                  {INDUSTRY_OPTIONS.map((ind) => {
                    const isSelected = field.value?.includes(ind);
                    return (
                      <button
                        key={ind}
                        type="button"
                        onClick={() => field.onChange(toggleArrayItem(field.value, ind))}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          isSelected
                            ? "bg-[hsl(265,40%,95%)] text-[hsl(265,45%,40%)] border-[hsl(265,30%,78%)] border shadow-soft"
                            : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
                        }`}
                      >
                        {ind}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setShowCustomIndustry(!showCustomIndustry)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      showCustomIndustry
                        ? "bg-[hsl(265,40%,95%)] text-[hsl(265,45%,40%)] border-[hsl(265,30%,78%)] border shadow-soft"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
                    }`}
                  >
                    + Other
                  </button>
                </div>
                {showCustomIndustry && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="Enter custom industry..."
                      value={customIndustry}
                      onChange={(e) => setCustomIndustry(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const trimmed = customIndustry.trim();
                          if (trimmed && !field.value?.includes(trimmed)) {
                            field.onChange([...(field.value || []), trimmed]);
                            setCustomIndustry("");
                          }
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const trimmed = customIndustry.trim();
                        if (trimmed && !field.value?.includes(trimmed)) {
                          field.onChange([...(field.value || []), trimmed]);
                          setCustomIndustry("");
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                )}
                {field.value && field.value.filter(v => !(INDUSTRY_OPTIONS as readonly string[]).includes(v)).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {field.value.filter(v => !(INDUSTRY_OPTIONS as readonly string[]).includes(v)).map((custom) => (
                      <span key={custom} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[hsl(265,40%,95%)] text-[hsl(265,45%,40%)] border border-[hsl(265,30%,88%)]">
                        {custom}
                        <button type="button" onClick={() => field.onChange(field.value?.filter(v => v !== custom))} className="hover:text-destructive">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </section>

        {/* Section 6: Social Links */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Connect
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormField
            control={form.control}
            name="linkedinUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  LinkedIn URL
                  <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </FormLabel>
                <FormControl>
                  <Input
                    type="url"
                    placeholder="https://linkedin.com/in/yourprofile"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="websiteUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Website URL
                  <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </FormLabel>
                <FormControl>
                  <Input
                    type="url"
                    placeholder="https://yourwebsite.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    placeholder="+358 40 123 4567"
                    autoComplete="tel"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Shared only with members you connect with
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </section>

        {/* Spacer so form content isn't hidden behind sticky footer */}
        <div className="pb-4" />

        {/* Sticky Submit Footer */}
        <div
          className="sticky bottom-0 z-10 -mx-6 px-6 sm:-mx-8 sm:px-8 pt-4 pb-4 bg-card border-t border-border/50 space-y-4"
          style={{ boxShadow: '0 -4px 12px rgba(0,0,0,0.06)' }}
        >
          <FormField
            control={form.control}
            name="legalAcceptanceConfirmed"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value === true}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-normal cursor-pointer">
                    I confirm I am eligible for FBN membership, and I agree to the{" "}
                    <a
                      href="/legal/fbn-international-terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-primary hover:text-primary/80 font-medium"
                    >
                      FBN International Terms of Use
                    </a>
                    , the{" "}
                    <a
                      href="/legal/fbn-international-privacy-notice"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-primary hover:text-primary/80 font-medium"
                    >
                      FBN International Privacy Notice
                    </a>
                    , the{" "}
                    <a
                      href="/legal/supplementary-terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-primary hover:text-primary/80 font-medium"
                    >
                      FBN Entrepreneurs Supplementary Terms of Use
                    </a>
                    , the{" "}
                    <a
                      href="/legal/supplementary-privacy-notice"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-primary hover:text-primary/80 font-medium"
                    >
                      FBN Entrepreneurs Supplementary Privacy Notice
                    </a>
                    , and the{" "}
                    <a
                      href="/legal/code-of-conduct"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-primary hover:text-primary/80 font-medium"
                    >
                      FBN Entrepreneurs Code of Conduct
                    </a>
                    .
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full py-6 text-lg"
            disabled={loading || form.watch("legalAcceptanceConfirmed") !== true}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Creating your account...
              </>
            ) : (
              "Join the Community"
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By signing up, you agree to our community guidelines and acknowledge that your profile information will be visible to other members.
          </p>
        </div>
      </form>
    </Form>
  );
};
